import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { sortByName, sortStrings } from "@/lib/sortRows";
import AppLayout from "@/components/AppLayout";
import InviteEmployeesModal from "@/components/InviteEmployeesModal";
import DataTable from "@/components/DataTable";
import TableSearch from "@/components/TableSearch";
import { statusPillClass } from "@/components/StatusBadge";
import { roleLabel } from "@/lib/roleLabels";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { UserPlusIcon, UserMinusIcon, ArrowClockwiseIcon } from "@phosphor-icons/react";

const ROLE_STYLE = {
  admin: "bg-[#0A0A0A] text-white",
  manager: "bg-[#0176D3] text-white",
  member: "bg-zinc-100 text-zinc-800",
  // "owner" (added 2026-09-18): a real account role with identical
  // permissions to manager (see is_manager_or_admin() in
  // schema_owner_role.sql) — the fixed initiative-owners allowlist
  // (lib/initiativeOwners.js) was migrated onto it so they read as "Owner"
  // here instead of "Manager"/"Member".
  owner: "bg-[#7C3AED] text-white",
};

const ALL_ROLES = ["member", "manager", "owner", "admin"];

export default function TeamPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [workload, setWorkload] = useState({});
  const [inviteOpen, setInviteOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);
  const [statusFilter, setStatusFilter] = useState("active");   // active | disabled | all
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [personNames, setPersonNames] = useState({});

  const isAdmin = user?.role === "admin";
  // "owner" has identical permissions to "manager" everywhere (see
  // is_manager_or_admin() in schema_owner_role.sql) — treated the same here.
  const isManager = user?.role === "manager" || user?.role === "owner";

  // "/team/members", not plain "/users": only this page's own list is
  // scoped to people who share a project with the caller (see
  // schema_team_page_project_scoping.sql and api.js) — task/project
  // assignee pickers etc. elsewhere still see everyone on purpose.
  const load = async () => {
    // The full roster only supplies names for the "Reports to" column — the
    // person someone reports to may not share a project with the caller, so
    // they can be absent from the scoped list above.
    const [u, s, all] = await Promise.all([api.get("/team/members"), api.get("/stats/overview"), api.get("/users")]);
    setUsers(sortByName(u.data));
    setPersonNames(Object.fromEntries((all.data || []).map((p) => [p._id, p.name || p.email])));
    const map = {};
    for (const w of s.data.workload) map[w.assignee_id] = w;
    setWorkload(map);
    setSelected(new Set());
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setRefreshing(false);
    }
  };

  // Status filter, then the search box, then alphabetical by name. Sorting last
  // means the order is stable whatever the other two are doing.
  const filtered = useMemo(() => {
    let rows = users;
    if (statusFilter === "active") rows = rows.filter((u) => !u.disabled);
    else if (statusFilter === "disabled") rows = rows.filter((u) => u.disabled);

    const needle = query.trim().toLowerCase();
    if (needle) {
      rows = rows.filter((u) =>
        `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(needle)
      );
    }

    /*
      A-Z is the RESTING order, not a mode.

      Sorting used to be a toolbar button that flipped between A-Z and Z-A.
      It is now the Member column header, like every other sortable column in
      the app — so this only has to establish the order the grid opens in.
      DataTable's sort takes over on the first header click and returns here
      when the reader clicks back to unsorted.

      localeCompare, not `<`: it sorts accented and mixed-case names the way a
      reader expects rather than by code point. Falls back to the email for a
      member who has no name yet, which is what the Member cell shows anyway.
    */
    const label = (u) => (u.name || u.email || "").trim();
    return [...rows].sort((a, b) =>
      label(a).localeCompare(label(b), undefined, { sensitivity: "base" }),
    );
  }, [users, statusFilter, query]);


  /*
    Select-all, over the rows CURRENTLY VISIBLE.

    `filtered` and not `users`, deliberately: ticking the header while a search
    or a status filter is active selects what the reader can actually see, not
    forty accounts they are not looking at. That is also why toggleAll replaces
    the selection outright rather than adding to it.

    The current admin's own row is excluded — they cannot deactivate themselves,
    so counting their row would make "all selected" unreachable and leave the
    header checkbox permanently half-ticked.
  */
  const selectableIds = filtered.filter((u) => u._id !== user?.id).map((u) => u._id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  const someSelected = selectableIds.some((id) => selected.has(id));

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(selectableIds));
  };

  // Mirrors protect_privileged_user_columns(): an admin can change anyone's
  // role; a manager can change member<->manager freely but can't touch a
  // Role changes are admin-only, full stop — a manager can invite new
  // members (see add-team-member) but cannot promote/demote an existing
  // account. Also block editing your own row from here to avoid an
  // accidental self-demotion with no one around to undo it.
  const canEditRole = (u) => u._id !== user?.id && isAdmin;
  const roleOptionsFor = () => ALL_ROLES;

  // Routed through an Edge Function (service role) rather than a direct
  // PostgREST PATCH — a same-table self-referential RLS check on
  // public.users (is this caller an admin/manager, looked up from the very
  // table being updated) turned out to silently match zero rows for real
  // requests, so the write never actually happened even though the client
  // saw no error. This mirrors add-team-member's already-proven pattern.
  const changeRole = async (u, role) => {
    if (role === u.role) return;
    try {
      const { data, error } = await supabase.functions.invoke("manage-team-member", {
        body: { action: "set_role", user_id: u._id, role },
      });
      if (error || data?.error) {
        let reason = data?.error;
        if (!reason && error?.context) {
          try { reason = (await error.context.clone().json())?.error; } catch { /* not JSON */ }
        }
        throw new Error(reason || error?.message || "unknown_error");
      }
      toast.success(`${u.name} is now ${roleLabel(role)}`);
      load();
    } catch (e) {
      toast.error(e.message || "Couldn't change role");
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const selectedUsers = useMemo(() => users.filter((u) => selected.has(u._id)), [users, selected]);
  const selectedAnyActive = selectedUsers.some((u) => !u.disabled);
  const selectedAnyDisabled = selectedUsers.some((u) => u.disabled);

  const invokeSetDisabled = async (user_ids, disabled) => {
    const { data, error } = await supabase.functions.invoke("manage-team-member", {
      body: { action: "set_disabled", user_ids, disabled },
    });
    if (error || data?.error) {
      let reason = data?.error;
      if (!reason && error?.context) {
        try { reason = (await error.context.clone().json())?.error; } catch { /* not JSON */ }
      }
      throw new Error(reason || error?.message || "unknown_error");
    }
    return data;
  };

  const bulkUpdate = async (disabled) => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const data = await invokeSetDisabled([...selected], disabled);
      toast.success(
        disabled
          ? `Disabled ${data.modified} member${data.modified === 1 ? "" : "s"}`
          : `Re-enabled ${data.modified} member${data.modified === 1 ? "" : "s"}`
      );
      if (data.skipped_self) toast.info("Your own account was skipped");
      await load();
    } catch (e) {
      toast.error(e.message || "Couldn't update status");
    } finally {
      setBusy(false);
      setConfirmDeactivate(false);
    }
  };

  const reactivateOne = async (u) => {
    setBusy(true);
    try {
      await invokeSetDisabled([u._id], false);
      toast.success(`${u.name} re-enabled`);
      await load();
    } catch (e) {
      toast.error(e.message || "Couldn't re-enable");
    } finally { setBusy(false); }
  };

  return (
    <AppLayout>
      <div className="trk-sticky-header sticky top-0 z-20 px-8 py-4 flex items-center justify-between">
        <div>
          <div className="trk-uppercase-label">People</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Team</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs font-mono text-zinc-500" data-testid="team-count">
            {filtered.length} {statusFilter === "disabled" ? "disabled" : statusFilter === "active" ? "active" : "total"}
          </div>

          <button
            onClick={refresh}
            disabled={refreshing}
            data-testid="team-refresh-btn"
            title="Refresh"
            className="p-2 border border-border rounded-md hover:bg-zinc-50 hover:border-[#0176D3] hover:text-[#0176D3] transition-colors disabled:opacity-50"
          >
            <ArrowClockwiseIcon size={14} weight="bold" className={refreshing ? "animate-spin" : ""} />
          </button>

          {/* Filter pills */}
          <div className="inline-flex border border-border rounded-md overflow-hidden bg-white" data-testid="team-filter">
            {["active", "disabled", "all"].map((k) => (
              <button
                key={k}
                onClick={() => { setStatusFilter(k); setSelected(new Set()); }}
                data-testid={`team-filter-${k}`}
                className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors ${
                  statusFilter === k ? "bg-[#0176D3] text-white" : "text-[#475569] hover:bg-zinc-50"
                }`}
              >{k}</button>
            ))}
          </div>

          {isAdmin && (
            <Link to="/team/audit" data-testid="audit-log-link"
                  className="text-xs font-mono uppercase tracking-wider text-[#475569] hover:text-[#0176D3] border border-border rounded-md px-3 py-1.5 hover:border-[#0176D3] transition-colors">
              Audit log
            </Link>
          )}
          {(isAdmin || isManager) && (
            <Button
              onClick={() => setInviteOpen(true)}
              data-testid="invite-employees-btn"
              className="bg-[#0176D3] hover:bg-[#005FB2] text-white h-9 rounded-md"
            >
              <UserPlusIcon size={16} weight="fill" className="mr-1.5" /> Add team member
            </Button>
          )}
        </div>
      </div>

      <div className="px-8 py-6 trk-fade-in">
        <DataTable
          /*
            KEY BUMPED from "team.visibleColumns".

            DataTable persists column ORDER as well as visibility, and a saved
            order outlives a code change — which is why the select column was
            rendering pinned on the far RIGHT for anyone who had ever dragged
            these headers, no matter that it is first in the array below. The
            column set is changing here anyway (select-all gone, Member now
            sortable), so discarding the stored layout is the honest fix and
            puts everybody back on the intended order. Widths and hidden
            columns reset with it, once.
          */
          storageKey="team.tableLayout.v2"
          toolbarStart={
            <TableSearch
              value={query}
              onChange={setQuery}
              placeholder="Search name or email…"
              label="Search team members"
              testid="team-search"
            />
          }
          toolbarExtra={
            <>
              {/*
                  Bulk actions sit at the RIGHT of the toolbar, immediately
                  left of the column picker — toolbarExtra renders into that
                  group. They appear only when something is selected, so the
                  toolbar is not carrying dead controls the rest of the time,
                  and the count sits next to the thing that produced it instead
                  of in a separate sticky band that pushed the whole grid down
                  every time a row was ticked.

                  AMBER, not the red it used to be. Deactivating is reversible
                  — Re-enable is right beside it and the account's work and
                  history are preserved — so red overstated it. Amber is the
                  app's existing "caution, not alarm" colour: #B45309 is the
                  same deep amber the Cancelled status carries.
              */}
              {isAdmin && selected.size > 0 && (
                <div className="inline-flex items-center gap-2 rounded-sm border border-amber-300 bg-amber-50 pl-3 pr-2 py-1 trk-fade-in"
                     data-testid="bulk-action-bar">
                  <span className="text-xs whitespace-nowrap">
                    <span className="font-mono font-bold text-amber-900">{selected.size}</span>
                    <span className="text-amber-800"> selected</span>
                  </span>
                  <button
                    onClick={() => setSelected(new Set())}
                    className="text-[10px] font-mono uppercase tracking-wider text-amber-700 hover:text-amber-900"
                    data-testid="bulk-clear-btn"
                  >Clear</button>
                  {selectedAnyDisabled && (
                    <Button
                      variant="outline"
                      onClick={() => bulkUpdate(false)}
                      disabled={busy}
                      data-testid="bulk-reactivate-btn"
                      className="h-7 rounded-sm px-2.5 text-xs bg-white border-[#059669] text-[#059669] hover:bg-[#DCFCE7]"
                    >
                      <ArrowClockwiseIcon size={12} weight="bold" className="mr-1" />
                      Re-enable
                    </Button>
                  )}
                  {selectedAnyActive && (
                    <Button
                      onClick={() => setConfirmDeactivate(true)}
                      disabled={busy}
                      data-testid="bulk-deactivate-btn"
                      /*
                        Translucent at rest, SOLID on hover. The amber panel
                        behind it shows through slightly, so the button sits in
                        the selection pill rather than sitting on top of it —
                        and firming up under the cursor keeps it feeling like a
                        button you are about to press rather than a faded one.
                      */
                      className="h-7 rounded-sm px-2.5 text-xs bg-[#B45309]/85 hover:bg-[#92400E] text-white transition-colors"
                    >
                      <UserMinusIcon size={12} weight="fill" className="mr-1" />
                      Deactivate
                    </Button>
                  )}
                </div>
              )}
            </>
          }
          testIdPrefix="team"
          rows={filtered}
          rowKey={(u) => u._id}
          pageSize={20}
          emptyMessage="No members match this filter."
          rowClassName={(u) =>
            `${selected.has(u._id) ? "bg-[#EFF6FF]" : "hover:bg-zinc-50"} ${u.disabled ? "opacity-70" : ""}`
          }
          columns={[
            // The select column only exists for an admin, and is never offered
            // in the picker — hiding your own bulk-select would be a trap.
            ...(isAdmin
              ? [{
                  key: "select", label: "Select", locked: true, hideFromPicker: true, cls: "pl-4 pr-2", weight: 40,
                  /*
                    Select-all lives in this column's header — the cell
                    immediately before Member, directly above the checkboxes it
                    controls, which is where every data grid puts it and where
                    the hand already is.

                    Safe to put an interactive control in a header here: the
                    column is `locked`, so DataTable makes it neither draggable
                    nor sortable and there is no header onClick for the click to
                    fight. self-center because that header row is
                    baseline-aligned for text, which a checkbox is not.

                    THREE STATES, not two: indeterminate (a dash) when some but
                    not all visible rows are ticked. Without it, ticking one row
                    leaves this box looking empty, which reads as "nothing is
                    selected" while the amber bar says otherwise.
                  */
                  head: (
                    <Checkbox
                      data-testid="team-select-all"
                      checked={allSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={toggleAll}
                      aria-label={allSelected ? "Clear selection" : "Select all"}
                      title={allSelected ? "Clear selection" : "Select all shown"}
                      disabled={selectableIds.length === 0}
                      className="self-center"
                    />
                  ),
                  cell: (u) => (
                    <Checkbox
                      data-testid={`team-check-${u._id}`}
                      checked={selected.has(u._id)}
                      onCheckedChange={() => toggleOne(u._id)}
                      /*
                        `u._id` against `user?.id`, and the asymmetry is
                        deliberate: a row fetched through lib/api.js is mapped
                        to `_id`, while the AuthContext user only ever has
                        `.id`. Comparing like for like matched nothing, so an
                        admin's own checkbox was never actually disabled.
                      */
                      disabled={u._id === user?.id}
                      aria-label={`Select ${u.name}`}
                    />
                  ),
                }]
              : []),
            {
              key: "member", label: "Member", locked: true, cls: "px-4",
              // Click the header to sort by name. Replaces the A-Z button that
              // used to sit in the toolbar; that slot now carries the bulk
              // actions instead.
              sortValue: (u) => (u.name || u.email || "").trim(),
              cell: (u) => (
                <div className="min-w-0">
                  <Link to={`/team/${u._id}`} className="block break-words font-medium hover:text-[#0176D3]" data-testid={`team-member-${u._id}`} title={u.name}>{u.name}</Link>
                  <div className="break-words text-xs text-zinc-500" title={u.email}>{u.email}</div>
                </div>
              ),
            },
            {
              // Who this person reports to (users.reports_to, supabase/schema_reports_to.sql).
              key: "reports_to", label: "Reports to", cls: "px-4", weight: 150,
              sortValue: (u) => personNames[u.reports_to] || "",
              cell: (u) => (
                <span className="text-sm text-zinc-700" data-testid={`team-reports-to-${u._id}`}>
                  {personNames[u.reports_to] || <span className="text-zinc-400">—</span>}
                </span>
              ),
            },
            {
              key: "role", label: "Role", cls: "px-4", weight: 132,
              cell: (u) =>
                canEditRole(u) ? (
                  <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                    <SelectTrigger className="rounded-sm h-7 text-[10px] uppercase font-mono w-[110px]" data-testid={`role-select-${u._id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {sortStrings(roleOptionsFor(u)).map((r) => <SelectItem key={r} value={r} className="uppercase font-mono text-[10px]">{roleLabel(r)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className={`inline-flex items-center rounded-sm px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${ROLE_STYLE[u.role] || ROLE_STYLE.member}`}>{roleLabel(u.role)}</span>
                ),
            },
            {
              key: "status", label: "Status", cls: "px-4", weight: 116,
              cell: (u) => (
                <span
                  className={`${statusPillClass()} px-2.5`}
                  data-testid={u.disabled ? `team-status-disabled-${u._id}` : `team-status-active-${u._id}`}
                >
                  <span
                    className="h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: u.disabled ? "#DC2626" : "#059669" }}
                  />
                  {u.disabled ? "Disabled" : "Active"}
                </span>
              ),
            },
            {
              key: "total", label: "Total", cls: "px-3 text-right", weight: 92,
              cell: (u) => <span className="font-mono tabular-nums">{(workload[u._id] || {}).total || 0}</span>,
            },
            {
              key: "wip", label: "WIP", cls: "px-3 text-right", weight: 92,
              cell: (u) => <span className="font-mono tabular-nums text-[#0176D3]">{(workload[u._id] || {}).wip || 0}</span>,
            },
            {
              key: "hold", label: "Hold", cls: "px-3 text-right", weight: 92,
              cell: (u) => <span className="font-mono tabular-nums text-amber-600">{(workload[u._id] || {}).hold || 0}</span>,
            },
            {
              key: "live", label: "Live", cls: "px-3 text-right", weight: 92,
              cell: (u) => <span className="font-mono tabular-nums text-emerald-600">{(workload[u._id] || {}).live || 0}</span>,
            },
            {
              key: "action", label: "Action", head: "", hideFromPicker: true, cls: "px-4 text-right", weight: 104,
              cell: (u) =>
                isAdmin && u.disabled ? (
                  <button
                    onClick={() => reactivateOne(u)}
                    disabled={busy}
                    data-testid={`team-reactivate-${u._id}`}
                    className="font-mono text-xs uppercase tracking-wider text-[#059669] hover:underline"
                  >Enable →</button>
                ) : (
                  <Link to={`/team/${u._id}`} className="font-mono text-xs uppercase tracking-wider text-[#0176D3] hover:underline">View →</Link>
                ),
            },
          ]}
        />
      </div>

      <InviteEmployeesModal
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={() => { load(); }}
        currentUserRole={user?.role}
      />

      <AlertDialog open={confirmDeactivate} onOpenChange={setConfirmDeactivate}>
        <AlertDialogContent data-testid="deactivate-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">
              Deactivate {selected.size} account{selected.size === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#475569]">
              Disabled members will no longer be able to sign in. Their tasks and history are preserved and you can re-enable them anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deactivate-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => bulkUpdate(true)}
              data-testid="deactivate-confirm"
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
            >Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
